import { memo, useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { getXTGrid } from '@4cm/core/tactics/xtGrid';

/**
 * 3D-native Expected Threat overlay. Same Karun Singh-style 12×16
 * grid the 2D layer renders, but as an InstancedMesh of plane cells
 * laid flat on the pitch at y≈0.007 so the 3D camera projects it
 * with real perspective.
 *
 * Per-instance colour follows the cool→hot ramp. Like Heatmap3D we
 * pre-multiply colour by intensity² + use additive blending so dim
 * cells fade naturally into the grass and hot zones glow.
 */

interface RampStop {
  t: number;
  rgb: [number, number, number];
}
const RAMP: RampStop[] = [
  { t: 0.0, rgb: [56, 130, 232] },
  { t: 0.4, rgb: [74, 222, 128] },
  { t: 0.7, rgb: [251, 191, 36] },
  { t: 1.0, rgb: [239, 68, 68] },
];

function rampColor(t: number): [number, number, number] {
  if (!(t > 0)) return RAMP[0]!.rgb;
  if (t >= 1) return RAMP[RAMP.length - 1]!.rgb;
  for (let i = 1; i < RAMP.length; i++) {
    const upper = RAMP[i]!;
    if (t <= upper.t) {
      const lower = RAMP[i - 1]!;
      const span = upper.t - lower.t;
      const f = span > 0 ? (t - lower.t) / span : 0;
      return [
        lower.rgb[0] + (upper.rgb[0] - lower.rgb[0]) * f,
        lower.rgb[1] + (upper.rgb[1] - lower.rgb[1]) * f,
        lower.rgb[2] + (upper.rgb[2] - lower.rgb[2]) * f,
      ];
    }
  }
  return RAMP[RAMP.length - 1]!.rgb;
}

const XT_NORMALISER = 0.28;
const CULL_THRESHOLD = 0.02;

export interface XTLayer3DProps {
  opacity: number;
}

const _dummy = new THREE.Object3D();
const _color = new THREE.Color();

function XTLayer3DInner({ opacity }: XTLayer3DProps) {
  const cells = useMemo(() => {
    const grid = getXTGrid();
    const items: Array<{ x: number; z: number; r: number; g: number; b: number }> = [];
    const { nRows, nCols, cellLengthM, cellWidthM, halfL, halfW, values } = grid;
    for (let r = 0; r < nRows; r++) {
      const cx = -halfW + (r + 0.5) * cellWidthM;
      for (let c = 0; c < nCols; c++) {
        const v = values[r * nCols + c]!;
        const norm = Math.min(1, v / XT_NORMALISER);
        if (norm < CULL_THRESHOLD) continue;
        const cz = -halfL + (c + 0.5) * cellLengthM;
        const [rr, gg, bb] = rampColor(norm);
        const k = norm * norm;
        items.push({
          x: cx,
          z: cz,
          r: (rr / 255) * k,
          g: (gg / 255) * k,
          b: (bb / 255) * k,
        });
      }
    }
    return { items, cellLengthM, cellWidthM };
  }, []);

  const geometry = useMemo(
    () =>
      new THREE.PlaneGeometry(cells.cellLengthM, cells.cellWidthM),
    [cells.cellLengthM, cells.cellWidthM],
  );
  const material = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        vertexColors: true,
        transparent: true,
        opacity,
        depthWrite: false,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
      }),
    [opacity],
  );

  const meshRef = useRef<THREE.InstancedMesh>(null);

  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    if (typeof mesh.setMatrixAt !== 'function') return;
    const { items } = cells;
    for (let i = 0; i < items.length; i++) {
      const item = items[i]!;
      _dummy.position.set(item.x, 0, item.z);
      _dummy.rotation.set(-Math.PI / 2, 0, 0);
      _dummy.scale.set(1, 1, 1);
      _dummy.updateMatrix();
      mesh.setMatrixAt(i, _dummy.matrix);
      _color.setRGB(item.r, item.g, item.b);
      mesh.setColorAt(i, _color);
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }, [cells]);

  if (cells.items.length === 0) return null;

  return (
    <instancedMesh
      ref={meshRef}
      args={[geometry, material, cells.items.length]}
      position={[0, 0.007, 0]}
      raycast={() => undefined}
      renderOrder={0}
    />
  );
}

export const XTLayer3D = memo(XTLayer3DInner);
